import {
  addEffect,
  addHandlers,
  addState,
  CurriedPropsAdder,
  flowMax,
} from 'ad-hok'
import { DependenciesArgument } from 'ad-hok/lib/helperTypes'
import LRU from 'lru-cache'
import { minutesToMilliseconds } from 'shared/src/utils/time'
import { typedAs } from 'shared/src/utils/typedAs'

export const NOT_LOADED = 'notLoaded'
export const LOADING_COLD = 'loadingCold'
export const LOADING = 'loading'
export const LOADED = 'loaded'
export const ERROR_COLD = 'errorCold'
export const ERROR = 'error'

export type DataLoadingCold = {
  state: typeof LOADING_COLD
}

export type DataLoading<TData> = {
  state: typeof LOADING
  data: TData
}

export type DataLoaded<TData> = {
  state: typeof LOADED
  data: TData
}

export type DataErrorCold = {
  state: typeof ERROR_COLD
  error: Error
}

export type DataError<TData> = {
  state: typeof ERROR
  error: Error
  data: TData
}

export const makeDataLoadingCold = (): DataLoadingCold => ({
  state: LOADING_COLD,
})

export const makeDataLoading = <TData>(data: TData): DataLoading<TData> => ({
  state: LOADING,
  data,
})

export const makeDataLoaded = <TData>(data: TData): DataLoaded<TData> => ({
  state: LOADED,
  data,
})

export const makeDataErrorCold = (error: Error): DataErrorCold => ({
  state: ERROR_COLD,
  error,
})

export const makeDataError = <TData>(
  error: Error,
  data: TData
): DataError<TData> => ({
  state: ERROR,
  error,
  data,
})

export type DataLoadingState<TData> =
  | DataLoadingCold
  | DataLoading<TData>
  | DataLoaded<TData>
  | DataErrorCold
  | DataError<TData>

export const isLoading = <TData>(
  state: DataLoadingState<TData>
): state is DataLoadingCold | DataLoading<TData> =>
  state.state === LOADING_COLD || state.state === LOADING

export const isError = <TData>(
  state: DataLoadingState<TData>
): state is DataErrorCold | DataError<TData> =>
  state.state === ERROR_COLD || state.state === ERROR

export const isErrorCold = <TData>(
  state: DataLoadingState<TData>
): state is DataErrorCold => state.state === ERROR_COLD

export const isLoaded = <TData>(
  state: DataLoadingState<TData>
): state is DataLoaded<TData> => state.state === LOADED

type DataLoadingHasData<TData> =
  | DataLoading<TData>
  | DataLoaded<TData>
  | DataError<TData>

export const hasData = <TData>(
  state: DataLoadingState<TData>
): state is DataLoadingHasData<TData> =>
  state.state === LOADING || state.state === LOADED || state.state === ERROR

export const mapDataLoadingState =
  <TInput, TOutput>(f: (data: TInput) => TOutput) =>
  (dataLoadingState: DataLoadingState<TInput>): DataLoadingState<TOutput> => {
    switch (dataLoadingState.state) {
      case LOADING_COLD:
        return makeDataLoadingCold()
      case LOADING:
        return makeDataLoading(f(dataLoadingState.data))
      case LOADED:
        return makeDataLoaded(f(dataLoadingState.data))
      case ERROR_COLD:
        return makeDataErrorCold(dataLoadingState.error)
      case ERROR:
        return makeDataError(dataLoadingState.error, f(dataLoadingState.data))
    }
  }

type AddDataLoadingFromPromise = <
  TProps extends {},
  TPropName extends string,
  TData
>(
  propName: TPropName,
  fetchData: (props: TProps) => Promise<TData>,
  dependencies: DependenciesArgument<TProps>,
  cacheKey?: (props: TProps) => string
) => CurriedPropsAdder<
  TProps,
  {
    [Key in TPropName]: DataLoadingState<TData>
  } & {
    [Key in `set${Capitalize<TPropName>}`]: (value: TData) => void
  } & {
    [Key in `reload${Capitalize<TPropName>}`]: () => Promise<void>
  }
>

const addHandlerPrefix = <TName extends string, TPrefix extends string>(
  name: TName,
  prefix: TPrefix
): `${TPrefix}${Capitalize<TName>}` => {
  const capitalizedName: Capitalize<TName> = (name.charAt(0).toUpperCase() +
    name.slice(1)) as any
  return `${prefix}${capitalizedName}`
}

const DATA_LOADING_CACHE_TTL = minutesToMilliseconds(5)

const DATA_LOADING_CACHE = new LRU({
  ttl: DATA_LOADING_CACHE_TTL,
  ttlAutopurge: true,
})

export const addDataLoadingFromPromise: AddDataLoadingFromPromise = (
  propName,
  fetchData,
  dependencies,
  cacheKey
) =>
  flowMax(
    (props) => props as any,
    addState(propName, 'setDataLoading', (props) => {
      const cachedData = cacheKey
        ? DATA_LOADING_CACHE.get(cacheKey(props))
        : undefined

      const state = cachedData
        ? makeDataLoading(cachedData)
        : makeDataLoadingCold()

      return typedAs<DataLoadingState<any>>(state)
    }),
    addHandlers({
      fetch: (props) => () =>
        fetchData(props)
          .then((data) => {
            if (!cacheKey) {
              return data
            }

            DATA_LOADING_CACHE.set(cacheKey(props), data)

            return data
          })
          .then(makeDataLoaded)
          .catch(makeDataErrorCold)
          .then(props.setDataLoading),
    }),
    addEffect(
      ({ fetch }) =>
        () => {
          fetch()
        },
      dependencies as any
    ),
    addHandlers({
      [addHandlerPrefix(propName, 'set')]:
        ({ setDataLoading }) =>
        (value: any) => {
          setDataLoading(makeDataLoaded(value))
        },
      [addHandlerPrefix(propName, 'reload')]:
        ({ fetch }) =>
        () =>
          fetch(),
    })
  )
